import { api, ApiError, apiRequest } from '@/lib/api';
import type { Certificate } from './schemas';

export interface UploadCertificatePayload {
  file: File;
  password: string;
}

export interface UploadCertificateError {
  kind: 'password' | 'file' | 'cnpj_mismatch' | 'other';
  message: string;
  status: number;
}

export const certificatesApi = {
  get: async (cnpjId: string): Promise<Certificate | null> => {
    try {
      return await api.get<Certificate>(`/cnpjs/${cnpjId}/certificate`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  upload: async (
    cnpjId: string,
    payload: UploadCertificatePayload,
  ): Promise<Certificate> => {
    // Não usamos apiRequest com FormData automático por causa do refresh cenário
    // mas apiRequest já suporta FormData. Usamos-o para manter refresh token.
    const form = new FormData();
    form.append('file', payload.file);
    form.append('password', payload.password);
    return apiRequest<Certificate>({
      method: 'POST',
      path: `/cnpjs/${cnpjId}/certificate`,
      body: form,
    });
  },

  remove: (cnpjId: string) => api.delete<void>(`/cnpjs/${cnpjId}/certificate`),
};

export function classifyUploadError(err: unknown): UploadCertificateError {
  if (!(err instanceof ApiError)) {
    return { kind: 'other', message: 'Não foi possível validar o certificado.', status: 0 };
  }
  const msg = err.message ?? '';
  if (err.status === 422 && /senha/i.test(msg)) {
    return { kind: 'password', message: 'Senha incorreta', status: err.status };
  }
  if (err.status === 422 && /cnpj/i.test(msg)) {
    return { kind: 'cnpj_mismatch', message: msg, status: err.status };
  }
  if (err.status === 422) {
    return { kind: 'file', message: msg || 'Arquivo inválido', status: err.status };
  }
  return { kind: 'other', message: msg || 'Não foi possível validar o certificado.', status: err.status };
}

